import { Button, Span } from '@pandiafi/design-system'
import { Text } from '@pandiafi/components'
import { Formik, Field, FieldProps, Form } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { PinCodeTextInput } from '../../Register/PinCodeTextInput'
import { PageTranslationSafeLabel } from '../../../components'
import { VerifyInvitedUserCodeInput } from '../../../../common/validations/invitation'
import {
  validationErrors,
  verificationCodeRegex,
} from '../../../../common/validations'

import { useAPIVerifyInvitedUserCode } from 'api/apiHooks/useAPIVerifyInvitedUserCode'
import { FormErrorSection } from 'views/Login/FormErrorSection'
import FormFieldWrapper from 'components/FormField'

interface Props {
  uuid: string
  email: string
  onSuccess: () => void
}

const validationSchema = yup.object({
  verificationCode: yup
    .string()
    .matches(verificationCodeRegex, validationErrors.invalid_pin)
    .required(validationErrors.invalid_pin),
})

export const InvitationVerifyCodeForm: React.FC<Props> = ({
  uuid,
  email,
  onSuccess,
}) => {
  const { t } = useTranslation(['registration'])

  const { mutate, isPending, isError } = useAPIVerifyInvitedUserCode(uuid, {
    onSuccess,
  })

  return (
    <Formik
      initialValues={{ verificationCode: '' }}
      onSubmit={(values) => mutate(values)}
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ isValid }) => (
        <Form className="flex flex-col gap-5">
          <Text size="l" weight="medium" element="h3">
            {t('registration:confirmEmailHeader')}
          </Text>
          <Span light>
            {t('registration:verificationCodeDescription')}{' '}
            <strong>{email}</strong>.
          </Span>

          <div className="flex flex-col items-center">
            <Field name="verificationCode">
              {({
                field,
                meta,
              }: FieldProps<
                VerifyInvitedUserCodeInput['verificationCode']
              >) => (
                <FormFieldWrapper
                  className="flex flex-col items-center"
                  name={field.name}
                  meta={meta}
                  label={t('registration:verificationCodeLabel')}
                >
                  <PinCodeTextInput
                    {...field}
                    disabled={isPending}
                    name="verificationCode"
                  />
                </FormFieldWrapper>
              )}
            </Field>
          </div>

          {isError && (
            <FormErrorSection error={t('registration:verificationCodeError')} />
          )}

          <Button
            loading={isPending}
            disabled={!isValid || isPending}
            type="submit"
          >
            <PageTranslationSafeLabel>
              {t('registration:verificationCodeSubmit')}
            </PageTranslationSafeLabel>
          </Button>
        </Form>
      )}
    </Formik>
  )
}
